import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Crown, LayoutDashboard, ListChecks, BarChart2, FolderOpen,
  MessageSquare, CreditCard, LogOut, Menu, X, ChevronRight, Bell
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const navItems = [
  { href: '/portal', icon: LayoutDashboard, label: 'Dashboard' },
  { href: '/portal/onboarding', icon: ListChecks, label: 'Onboarding' },
  { href: '/portal/reports', icon: BarChart2, label: 'Rapportages' },
  { href: '/portal/files', icon: FolderOpen, label: 'Bestanden' },
  { href: '/portal/messages', icon: MessageSquare, label: 'Berichten' },
  { href: '/portal/billing', icon: CreditCard, label: 'Facturen' },
];

export default function PortalLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => { logout(); navigate('/login'); };

  const Sidebar = ({ mobile = false }) => (
    <aside className={`${mobile ? 'flex' : 'hidden lg:flex'} flex-col w-64 bg-[#0f172a] border-r border-white/5 min-h-screen`}>
      {/* Logo */}
      <div className="p-6 border-b border-white/5">
        <Link to="/platform" className="flex items-center gap-3">
          <div className="w-9 h-9 bg-[#D4A017] rounded-lg flex items-center justify-center">
            <Crown className="w-5 h-5 text-[#0f172a]" />
          </div>
          <div>
            <p className="text-white font-bold text-sm font-serif">IMPERIA AI</p>
            <p className="text-[#D4A017] text-xs">Klantportaal</p>
          </div>
        </Link>
      </div>

      {/* Company */}
      <div className="px-4 py-3 mx-3 my-3 bg-white/5 rounded-xl">
        <p className="text-[#94a3b8] text-xs uppercase tracking-wider mb-1">Ingelogd als</p>
        <p className="text-white font-semibold text-sm truncate">{user?.name || 'Klant'}</p>
        <p className="text-[#D4A017] text-xs truncate">{user?.email}</p>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-2 space-y-1">
        {navItems.map(({ href, icon: Icon, label }) => {
          const active = location.pathname === href;
          return (
            <Link
              key={href}
              to={href}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                active
                  ? 'bg-[#D4A017] text-[#0f172a]'
                  : 'text-[#64748b] hover:text-white hover:bg-white/5'
              }`}
            >
              <Icon size={18} />
              {label}
              {active && <ChevronRight size={14} className="ml-auto" />}
            </Link>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="p-3 border-t border-white/5">
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-[#64748b] hover:text-red-400 hover:bg-red-500/10 text-sm font-medium transition-all"
        >
          <LogOut size={18} />
          Uitloggen
        </button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen bg-[#0a0f1e] flex">
      {/* Desktop sidebar */}
      <Sidebar />

      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setSidebarOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-64">
            <Sidebar mobile />
          </div>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Topbar */}
        <header className="h-16 bg-[#0f172a] border-b border-white/5 flex items-center justify-between px-6">
          <button className="lg:hidden text-[#64748b] hover:text-white" onClick={() => setSidebarOpen(true)}>
            <Menu size={22} />
          </button>
          <div className="flex items-center gap-4 ml-auto">
            <button className="relative text-[#64748b] hover:text-[#D4A017] transition-colors">
              <Bell size={20} />
            </button>
            <div className="w-8 h-8 bg-[#D4A017] rounded-full flex items-center justify-center text-[#0f172a] font-bold text-sm">
              {(user?.name || 'K')[0].toUpperCase()}
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-6 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
